import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSupplierAuth } from '../../../context/SupplierAuthContext';
import { supplierModel } from '../../../models/supplierModel';
import { getSupplierApplications } from '../../../models/procurementModel';
import { getSettings } from '../../../models/settingsModel';
import { validateToken, clearCorruptedTokens } from '../../../utils/tokenUtils';
import SupplierLogoUpload from '../../../components/supplier/SupplierLogoUpload';

function SupplierDashboard() {
  const { supplier, clearCorruptedToken } = useSupplierAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddressEdit, setShowAddressEdit] = useState(false);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [showLogoUpload, setShowLogoUpload] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showCompanyEdit, setShowCompanyEdit] = useState(false);
  
  // Password setup state
  const [passwordSetupData, setPasswordSetupData] = useState({
    name: '',
    position: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  
  // Application status tracking for document validity
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  
  // Form data
  const [addressForm, setAddressForm] = useState({
    address: '',
    phone: '',
    tel: ''
  });

  // Email management state
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [showEditEmail, setShowEditEmail] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [emailForm, setEmailForm] = useState({
    email: '',
    canLogin: false,
    viewOnly: false,
    sendLink: false
  });
  const [currentEmailData, setCurrentEmailData] = useState(null);

  // Profile edit state
  const [profileEditForm, setProfileEditForm] = useState({
    name: '',
    position: '',
    password: '',
    confirmPassword: ''
  });
  const [profileEditErrors, setProfileEditErrors] = useState({});

  // Company edit state
  const [companyEditForm, setCompanyEditForm] = useState({
    BP_Name: '',
    Company_Registered_Name: '',
    Contact_Person: '',
    Company_Reg: '',
    Vat_Registration: ''
  });
  const [companyEditErrors, setCompanyEditErrors] = useState({});

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsData = await getSettings();
        setSettings(settingsData || {}); 
      } catch (error) {
        console.error('Error loading settings:', error);
        setSettings({});
      }
    };
    loadSettings();
  }, []);

  // Fetch application status for document validity check
  const fetchApplicationStatus = async () => {
    try {
      setStatusLoading(true);
      
      const bpCode = String(dashboardData?.BP_Code || supplier?.BP_Code || '');
      if (!bpCode) {
        setApplicationStatus(null);
        return;
      }

      const response = await getSupplierApplications({
        search: bpCode,
        limit: 1,
        page: 1
      });

      if (response && response.data && response.data.length > 0) {
        const application = response.data.find(app => String(app.BP_Code) === bpCode);
        setApplicationStatus(application);
      } else {
        setApplicationStatus(null);
      }
    } catch (error) {
      console.error('Error fetching application status:', error);
      setApplicationStatus(null);
    } finally {
      setStatusLoading(false);
    }
  };

  // Check if user needs password setup
  useEffect(() => {
    const checkPasswordSetup = async () => {
      const needsPasswordSetup = localStorage.getItem('supplier_needs_password_setup');
      if (needsPasswordSetup === 'true' && supplier) {
        setShowPasswordSetup(true);
        localStorage.removeItem('supplier_needs_password_setup');
      }
    };
    
    if (supplier) {
      checkPasswordSetup();
    }
  }, [supplier]);

  // Helper functions
  const parseEmails = (emailString) => {
    if (!emailString) return [];
    if (emailString.includes(',')) {
      return emailString.split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0); // Filter out empty strings
    }
    const trimmed = emailString.trim();
    return trimmed.length > 0 ? [trimmed] : [];
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Fetch application status when dashboard data is available
  useEffect(() => {
    if (dashboardData && dashboardData.BP_Code) {
      fetchApplicationStatus();
    }
  }, [dashboardData]);

  // Listen for profile edit event from header menu
  useEffect(() => {
    const handleOpenProfileEdit = () => {
      setShowProfileEdit(true);
    };

    window.addEventListener('openProfileEdit', handleOpenProfileEdit);
    return () => {
      window.removeEventListener('openProfileEdit', handleOpenProfileEdit);
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('supplier_token');
      if (!validateToken(token)) {
        clearCorruptedTokens();
        clearCorruptedToken();
        return;
      }
      
      const response = await supplierModel.getDashboardData();

      if (response) {
        const actualData = response.data || response;
        setDashboardData(actualData);
        
        // Initialize address form with current data
        setAddressForm({
          address: actualData.Address || '',
          phone: actualData.Mobile_Phone || '',
          tel: actualData.Tel || ''
        });
      }
    } catch (error) {
      console.error('Dashboard error:', error.message);
      
      if (error.message && error.message.includes('authentication')) {
        clearCorruptedTokens();
        clearCorruptedToken();
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Address form handlers
  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await supplierModel.updateAddress(addressForm);
      toast.success('Address updated successfully');
      setShowAddressEdit(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating address:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to update address';
      toast.error(errorMessage);
    }
  };

  // Password setup handlers
  const handlePasswordSetupChange = (e) => {
    const { name, value } = e.target;
    setPasswordSetupData(prev => ({
      ...prev,
      [name]: value
    }));

    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    if (name === 'confirmPassword' || name === 'password') {
      const password = name === 'password' ? value : passwordSetupData.password;
      const confirmPassword = name === 'confirmPassword' ? value : passwordSetupData.confirmPassword;
      
      if (confirmPassword && password !== confirmPassword) {
        setPasswordErrors(prev => ({
          ...prev,
          confirmPassword: 'Passwords do not match'
        }));
      } else {
        setPasswordErrors(prev => ({
          ...prev,
          confirmPassword: ''
        }));
      }
    }
  };

  const handlePasswordSetupSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    if (!passwordSetupData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!passwordSetupData.position.trim()) {
      errors.position = 'Position is required';
    }
    if (!passwordSetupData.password) {
      errors.password = 'Password is required';
    } else if (passwordSetupData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    if (passwordSetupData.password !== passwordSetupData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    try {
      const response = await supplierModel.setupProfile({
        name: passwordSetupData.name,
        position: passwordSetupData.position,
        password: passwordSetupData.password
      });

      if (response.status === 1) {
        toast.success('Profile setup completed successfully!');
        setShowPasswordSetup(false);
        fetchDashboardData();
      } else {
        toast.error(response.message || 'Failed to setup profile');
      }
    } catch (error) {
      console.error('Password setup error:', error);
      toast.error('An error occurred while setting up your profile');
    }
  };

  // Profile edit handler
  const handleProfileEditSubmit = async (e) => {
    e.preventDefault();
    
    const errors = {};
    if (!profileEditForm.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!profileEditForm.position.trim()) {
      errors.position = 'Position is required';
    }

    // Validate password if provided
    if (profileEditForm.password || profileEditForm.confirmPassword) {
      if (!profileEditForm.password) {
        errors.password = 'Password is required';
      } else if (profileEditForm.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
      if (profileEditForm.password !== profileEditForm.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    if (Object.keys(errors).length > 0) {
      setProfileEditErrors(errors);
      return;
    }

    try {
      const updateData = {
        name: profileEditForm.name,
        position: profileEditForm.position
      };

      // Only include password if it was provided
      if (profileEditForm.password) {
        updateData.password = profileEditForm.password;
      }

      const response = await supplierModel.updateProfile(updateData);

      if (response && response.status === 1) {
        toast.success('Profile updated successfully!');
        setShowProfileEdit(false);
        setProfileEditErrors({});
        fetchDashboardData();
      } else {
        toast.error(response?.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'An error occurred while updating your profile';
      toast.error(errorMessage);
    }
  };

  // Company edit handler
  const handleCompanyEditSubmit = async (e) => {
    e.preventDefault();
    
    const errors = {};
    if (!companyEditForm.BP_Name.trim()) {
      errors.BP_Name = 'Business name is required';
    }
    if (!companyEditForm.Contact_Person.trim()) {
      errors.Contact_Person = 'Contact person is required';
    }

    if (Object.keys(errors).length > 0) {
      setCompanyEditErrors(errors);
      return;
    }

    try {
      const response = await supplierModel.updateCompanyDetails(companyEditForm);

      if (response && response.status === 1) {
        toast.success('Company details updated successfully!');
        setShowCompanyEdit(false);
        setCompanyEditErrors({});
        fetchDashboardData();
      } else {
        toast.error(response?.message || 'Failed to update company details');
      }
    } catch (error) {
      console.error('Company details update error:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'An error occurred while updating company details';
      toast.error(errorMessage);
    }
  };

  // Email management handlers
  const handleAddEmail = () => {
    setEmailForm({
      email: '',
      canLogin: false,
      viewOnly: false,
      sendLink: false
    });
    setShowAddEmail(true);
  };

  const handleEditEmail = (email) => {
    // Find user data for this email
    const userData = dashboardData?.users?.find(u => u.supplier_user_email === email.trim()) || {};
    
    setSelectedEmail(email);
    setCurrentEmailData(userData);
    setEmailForm({
      email: email,
      canLogin: userData.supplier_user_status === 1 || userData.supplier_user_status === '1',
      viewOnly: userData.supplier_user_view_only === 1 || userData.supplier_user_view_only === '1',
      sendLink: false
    });
    setShowEditEmail(true);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await supplierModel.addEmail(emailForm);
      
      // Check if the response indicates success
      if (response && response.status === 1) {
        toast.success('Email added successfully');
        setShowAddEmail(false);
        fetchDashboardData();
      } else {
        toast.error(response?.message || 'Failed to add email');
      }
    } catch (error) {
      console.error('Error adding email:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to add email';
      toast.error(errorMessage);
    }
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...emailForm,
        current_email: selectedEmail
      };
      const response = await supplierModel.updateEmail(updateData);
      
      // Check if the response indicates success
      if (response && response.status === 1) {
        toast.success('Email updated successfully');
        setShowEditEmail(false);
        fetchDashboardData();
      } else {
        toast.error(response?.message || 'Failed to update email');
      }
    } catch (error) {
      console.error('Error updating email:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to update email';
      toast.error(errorMessage);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // No data state
  if (!dashboardData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h4 className="text-xl font-semibold text-gray-800 mb-2">No data available</h4>
            <p className="text-gray-600 mb-6">Unable to load dashboard data.</p>
            <button 
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition duration-200"
              onClick={fetchDashboardData}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Safe data extraction
  const supplierData = dashboardData || {};
  const emails = parseEmails(supplierData.E_Mail) || [];
  const canAddEmail = emails.length < 3;
  const isViewOnly = supplier?.supplier_user_view_only === '1' || supplier?.supplier_user_view_only === 1;

  try {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome Header with Account Status */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold text-gray-900">
                    Welcome, {supplierData?.BP_Name || 'Supplier'}
                  </h1>
                  {/* Account Status Indicator */}
                  {(() => {
                    const isApproved = supplierData?.Status === 1 || supplierData?.Status === '1';
                    const statusColor = isApproved ? 'green' : 'yellow';
                    const statusText = isApproved ? 'Approved' : 'Pending Approval';
                    const statusIcon = isApproved ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    );
                    return (
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-${statusColor}-100 text-${statusColor}-800 border border-${statusColor}-200`}>
                        {statusIcon}
                        {statusText}
                      </span>
                    );
                  })()}
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  Manage your supplier profile, view opportunities, and track your business status
                </p>
              </div>
              
              {/* Company Logo */}
              <div className="hidden sm:block">
                <div className="relative group">
                  {supplierData?.supplier_logo ? (
                    <div className="flex flex-col items-center gap-2">
                      <img 
                        src={supplierData.supplier_logo.base64 || supplierData.supplier_logo.file_url} 
                        alt="Company Logo"
                        className="h-24 w-auto rounded-lg object-contain border-2 border-gray-200 bg-white p-2"
                        onError={(e) => {
                          console.error('Logo failed to load');
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        onClick={() => setShowLogoUpload(true)}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Update Logo
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-24 w-24 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                        <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <button
                        onClick={() => setShowLogoUpload(true)}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Upload Logo
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Document Status Alert */}
          {!statusLoading && applicationStatus && (
            applicationStatus.application_status === 2 || 
            applicationStatus.application_status === '2'
          ) && (
            <div className="mb-8 bg-orange-50 border-l-4 border-orange-400 p-4 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-orange-800">Missing Documents Required</h3>
                  <div className="mt-2 text-sm text-orange-700">
                    <p>Your supplier application is incomplete. Please upload the required documents to complete your application process.</p>
                  </div>
                  <div className="mt-4">
                    <Link
                      to="/supplier/documents"
                      className="bg-orange-100 px-3 py-1.5 rounded-md text-sm font-medium text-orange-800 hover:bg-orange-200 transition-colors"
                    >
                      Upload Documents
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notice for Unapproved Suppliers */}
          {(supplierData?.Status === 0 || supplierData?.Status === '0') && (
            <div className="mb-8 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Account Pending Approval</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>Your supplier account is pending approval. While you wait:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>You can edit your company details including the company name</li>
                      <li>Upload or update required documents</li>
                      <li>Review and update your contact information</li>
                    </ul>
                    <p className="mt-2 font-semibold">⚠️ Important: You can only edit the company name before approval. Once approved, the company name becomes permanent.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column - Company Info & Profile */}
            <div className="lg:col-span-2 space-y-6">
              {/* Company Information */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Company Information</h3>
                    <div className="flex gap-2">
                      {!isViewOnly && (supplierData?.Status === 0 || supplierData?.Status === '0') && (
                        <button
                          onClick={() => {
                            setCompanyEditForm({
                              BP_Name: supplierData?.BP_Name || '',
                              Company_Registered_Name: supplierData?.Company_Registered_Name || '',
                              Contact_Person: supplierData?.Contact_Person || '',
                              Company_Reg: supplierData?.Company_Reg || '',
                              Vat_Registration: supplierData?.Vat_Registration || ''
                            });
                            setCompanyEditErrors({});
                            setShowCompanyEdit(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 text-sm font-medium flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit Details
                        </button>
                      )}
                      {!isViewOnly && (
                        <button
                          onClick={() => setShowAddressEdit(true)}
                          className="text-indigo-600 hover:text-indigo-900 text-sm font-medium flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          Edit Contact
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Business Name</dt>
                      <dd className="mt-1 text-sm text-gray-900">{supplierData?.BP_Name || 'Not provided'}</dd>
                    </div>
                    
                    {supplierData?.Company_Registered_Name && (
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-gray-500">Registered Company Name</dt>
                        <dd className="mt-1 text-sm text-gray-900">{supplierData.Company_Registered_Name}</dd>
                      </div>
                    )}
                    
                    {supplierData?.Contact_Person && (
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-gray-500">Contact Person</dt>
                        <dd className="mt-1 text-sm text-gray-900">{supplierData.Contact_Person}</dd>
                      </div>
                    )}
                    
                    {supplierData?.Company_Reg && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Registration Number</dt>
                        <dd className="mt-1 text-sm text-gray-900">{supplierData.Company_Reg}</dd>
                      </div>
                    )}
                    
                    {supplierData?.Vat_Registration && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">VAT Number</dt>
                        <dd className="mt-1 text-sm text-gray-900">{supplierData.Vat_Registration}</dd>
                      </div>
                    )}
                    
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Address</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{supplierData?.Address || 'No address provided'}</dd>
                    </div>
                    
                    {supplierData?.Tel && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Office Phone</dt>
                        <dd className="mt-1 text-sm text-gray-900">{supplierData.Tel}</dd>
                      </div>
                    )}
                    
                    {supplierData?.Mobile_Phone && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Mobile Phone</dt>
                        <dd className="mt-1 text-sm text-gray-900">{supplierData.Mobile_Phone}</dd>
                      </div>
                    )}
                    
                    {supplierData?.Group_Code && (
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-gray-500">Business Categories</dt>
                        <dd className="mt-1 text-sm text-gray-900">{supplierData.Group_Code.replace(/,/g, ', ')}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>

              {/* Email Management */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Email Addresses</h3>
                    {canAddEmail && !isViewOnly && (
                      <button
                        onClick={handleAddEmail}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Email
                      </button>
                    )}
                  </div>
                  
                  <div>
                    {emails.length > 0 ? (
                      <ul className="divide-y divide-gray-200">
                        {emails.map((email, index) => (
                          <li key={index} className="py-3 flex justify-between items-center">
                            <div className="flex items-center">
                              <svg className="flex-shrink-0 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                              </svg>
                              <span className="ml-3 text-sm text-gray-900">{email}</span>
                            </div>
                            {!isViewOnly && (
                              <button
                                onClick={() => handleEditEmail(email)}
                                className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                              >
                                Edit
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-center py-8">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No email addresses</h3>
                        <p className="mt-1 text-sm text-gray-500">Get started by adding an email address.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Document Status & Quick Actions */}
            <div className="space-y-6">
              {/* Document Status */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Document Status</h3>
                  <div className="space-y-4">
                    {/* Helper function to calculate days remaining and status */}
                    {(() => {
                      const calculateStatus = (expiryDateStr) => {
                        if (!expiryDateStr || expiryDateStr.trim() === '') {
                          return { status: 'missing', daysRemaining: 0, color: 'gray', width: '0%', label: 'Not Provided' };
                        }

                        // Parse date - handle both DD/MM/YYYY and YYYY-MM-DD formats
                        let expiryDate;
                        if (expiryDateStr.includes('/')) {
                          const [day, month, year] = expiryDateStr.split('/');
                          expiryDate = new Date(year, month - 1, day);
                        } else {
                          expiryDate = new Date(expiryDateStr);
                        }

                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        expiryDate.setHours(0, 0, 0, 0);

                        const daysRemaining = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

                        if (daysRemaining < 0) {
                          return { 
                            status: 'expired', 
                            daysRemaining: Math.abs(daysRemaining), 
                            color: 'red', 
                            width: '100%', 
                            label: `Expired ${Math.abs(daysRemaining)} days ago`,
                            date: expiryDateStr
                          };
                        } else if (daysRemaining <= 30) {
                          return { 
                            status: 'expiring', 
                            daysRemaining, 
                            color: 'red', 
                            width: `${Math.max((daysRemaining / 30) * 100, 10)}%`, 
                            label: `${daysRemaining} days remaining`,
                            date: expiryDateStr
                          };
                        } else if (daysRemaining <= 90) {
                          return { 
                            status: 'warning', 
                            daysRemaining, 
                            color: 'yellow', 
                            width: `${Math.max((daysRemaining / 365) * 100, 30)}%`, 
                            label: `${daysRemaining} days remaining`,
                            date: expiryDateStr
                          };
                        } else {
                          return { 
                            status: 'valid', 
                            daysRemaining, 
                            color: 'green', 
                            width: `${Math.min((daysRemaining / 365) * 100, 100)}%`, 
                            label: `${daysRemaining} days remaining`,
                            date: expiryDateStr
                          };
                        }
                      };

                      const taxStatus = calculateStatus(supplierData?.Tax_Certificate_Expiry_Date);
                      const beeStatus = calculateStatus(supplierData?.BBBEE_Expiration_Date);
                      const coidaStatus = calculateStatus(supplierData?.COIDA_Certificate_Expiry_Date);

                      return (
                        <>
                          {/* Tax Clearance */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-700">Tax Clearance</span>
                              <span className={`text-sm text-${taxStatus.color}-600`}>{taxStatus.label}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className={`bg-${taxStatus.color}-500 h-2 rounded-full`} style={{width: taxStatus.width}}></div>
                            </div>
                            {taxStatus.date && (
                              <p className={`text-xs text-${taxStatus.color}-500 mt-1`}>
                                {taxStatus.status === 'expired' ? 'Expired on' : 'Valid until'} {taxStatus.date}
                              </p>
                            )}
                          </div>

                          {/* BEE Certificate */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-700">BEE Certificate</span>
                              <span className={`text-sm text-${beeStatus.color}-600`}>{beeStatus.label}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className={`bg-${beeStatus.color}-500 h-2 rounded-full`} style={{width: beeStatus.width}}></div>
                            </div>
                            {beeStatus.date && (
                              <p className={`text-xs text-${beeStatus.color}-500 mt-1`}>
                                {beeStatus.status === 'expired' ? 'Expired on' : 'Valid until'} {beeStatus.date}
                              </p>
                            )}
                          </div>

                          {/* COIDA Certificate */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-700">COIDA Certificate</span>
                              <span className={`text-sm text-${coidaStatus.color}-600`}>{coidaStatus.label}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className={`bg-${coidaStatus.color}-500 h-2 rounded-full`} style={{width: coidaStatus.width}}></div>
                            </div>
                            {coidaStatus.date && (
                              <p className={`text-xs text-${coidaStatus.color}-500 mt-1`}>
                                {coidaStatus.status === 'expired' ? 'Expired on' : 'Valid until'} {coidaStatus.date}
                              </p>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div> 
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <Link
                      to="/supplier/invitations"
                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                      View Invitations
                    </Link>
                    
                    <Link
                      to="/supplier/quotations"
                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Submit Quotation
                    </Link>
                    
                    <Link
                      to="/supplier/documents"
                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m-3 3h6m-9-3h3m9 3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Update Documents
                    </Link>
                    
                    {!isViewOnly && (
                      <button
                        onClick={() => {
                          // Populate form with current user data
                          setProfileEditForm({
                            name: dashboardData?.current_user?.supplier_user_name || '',
                            position: dashboardData?.current_user?.supplier_user_position || '',
                            password: '',
                            confirmPassword: ''
                          });
                          setProfileEditErrors({});
                          setShowProfileEdit(true);
                        }}
                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        Edit Profile
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Debug Button */}
        {settings?.debug_mode === '1' && (
          <div className="mb-4">
            <button
              onClick={() => setShowDebugModal(true)}
              className="bg-red-600 text-white px-3 py-2 rounded-lg shadow-lg hover:bg-red-700 transition-colors text-xs font-medium"
              title="Debug Information"
            >
              🐛 Debug
            </button>
          </div>
        )}

        {/* Address Edit Modal */}
        {showAddressEdit && (
          <div className="fixed inset-0 z-50 overflow-y-auto backdrop-blur-sm">
            <div className="flex items-center justify-center min-h-screen px-4 py-6">
              <div 
                className="fixed inset-0 bg-gradient-to-br from-gray-900/60 via-gray-900/50 to-gray-900/60 transition-opacity" 
                onClick={() => setShowAddressEdit(false)} 
              />
              
              <div className="relative bg-white rounded-xl shadow-2xl transform transition-all w-full max-w-md border border-gray-100">
                <form onSubmit={handleAddressSubmit}>
                  {/* Header with Gradient */}
                  <div className="relative px-5 py-3.5 bg-gradient-primary rounded-t-xl compact-header">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-white">Edit Contact Information</h3>
                      </div>
                      <button
                        type="button"
                        className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-1 transition-all duration-200"
                        onClick={() => setShowAddressEdit(false)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  {/* Body with Enhanced Styling */}
                  <div className="px-5 py-4 space-y-4">
                    {/* Address Field */}
                    <div>
                      <label htmlFor="address" className="block text-sm font-semibold text-gray-800 mb-1.5">
                        Address
                      </label>
                      <div className="relative">
                        <div className="absolute top-3 left-3 pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <textarea 
                          id="address"
                          className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm bg-gray-50 focus:bg-white resize-none" 
                          name="address"
                          value={addressForm.address}
                          onChange={(e) => setAddressForm({...addressForm, address: e.target.value})}
                          required
                          rows={3}
                          placeholder="Enter complete address"
                        />
                      </div>
                    </div>

                    {/* Cellphone Number */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-800 mb-1.5">
                        Cellphone Number
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <input 
                          type="text" 
                          id="phone"
                          className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm bg-gray-50 focus:bg-white" 
                          name="phone"
                          value={addressForm.phone}
                          onChange={(e) => setAddressForm({...addressForm, phone: e.target.value})}
                          required
                          placeholder="e.g., 063 570 1919"
                        />
                      </div>
                    </div>

                    {/* Office Number */}
                    <div>
                      <label htmlFor="tel" className="block text-sm font-semibold text-gray-800 mb-1.5">
                        Office Number
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                        <input 
                          type="text" 
                          id="tel"
                          className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm bg-gray-50 focus:bg-white" 
                          name="tel"
                          value={addressForm.tel}
                          onChange={(e) => setAddressForm({...addressForm, tel: e.target.value})}
                          placeholder="e.g., 0987654321 (optional)"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer with Gradient */}
                  <div className="px-5 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 rounded-b-xl flex justify-end gap-2.5">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all"
                      onClick={() => setShowAddressEdit(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="px-5 py-2 text-sm font-semibold text-white bg-gradient-primary border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all shadow-md hover:shadow-lg compact-button"
                    >
                      <span className="flex items-center gap-1.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save Changes
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Company Edit Modal */}
        {showCompanyEdit && (
          <div className="fixed inset-0 z-50 overflow-y-auto backdrop-blur-sm">
            <div className="flex items-center justify-center min-h-screen px-4 py-6">
              <div 
                className="fixed inset-0 bg-gradient-to-br from-gray-900/60 via-gray-900/50 to-gray-900/60 transition-opacity" 
                onClick={() => setShowCompanyEdit(false)} 
              />
              
              <div className="relative bg-white rounded-xl shadow-2xl transform transition-all w-full max-w-2xl border border-gray-100">
                <form onSubmit={handleCompanyEditSubmit}>
                  {/* Header with Gradient */}
                  <div className="relative px-5 py-3.5 bg-gradient-purple rounded-t-xl compact-header">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">Edit Company Details</h3>
                          <p className="text-xs text-white/80 mt-0.5">⚠️ Company name can only be changed before approval</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-1 transition-all duration-200"
                        onClick={() => setShowCompanyEdit(false)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  {/* Body with Enhanced Styling */}
                  <div className="px-5 py-4 space-y-4">
                    {/* Business Name */}
                    <div>
                      <label htmlFor="BP_Name" className="block text-sm font-semibold text-gray-800 mb-1.5">
                        Business/Trading Name *
                      </label>
                      <div className="relative">
                        <div className="absolute top-3 left-3 pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <input 
                          type="text" 
                          id="BP_Name"
                          className={`w-full pl-9 pr-3 py-2 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm bg-gray-50 focus:bg-white ${
                            companyEditErrors.BP_Name ? 'border-red-500' : 'border-gray-200'
                          }`}
                          name="BP_Name"
                          value={companyEditForm.BP_Name}
                          onChange={(e) => {
                            setCompanyEditForm({...companyEditForm, BP_Name: e.target.value});
                            setCompanyEditErrors({...companyEditErrors, BP_Name: ''});
                          }}
                          required
                          placeholder="e.g., ABC Construction Ltd"
                        />
                      </div>
                      {companyEditErrors.BP_Name && (
                        <p className="text-red-500 text-xs mt-1">{companyEditErrors.BP_Name}</p>
                      )}
                    </div>

                    {/* Registered Company Name */}
                    <div>
                      <label htmlFor="Company_Registered_Name" className="block text-sm font-semibold text-gray-800 mb-1.5">
                        Registered Company Name
                      </label>
                      <div className="relative">
                        <div className="absolute top-3 left-3 pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <input 
                          type="text" 
                          id="Company_Registered_Name"
                          className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm bg-gray-50 focus:bg-white"
                          name="Company_Registered_Name"
                          value={companyEditForm.Company_Registered_Name}
                          onChange={(e) => setCompanyEditForm({...companyEditForm, Company_Registered_Name: e.target.value})}
                          placeholder="Official registered name (if different)"
                        />
                      </div>
                    </div>

                    {/* Contact Person */}
                    <div>
                      <label htmlFor="Contact_Person" className="block text-sm font-semibold text-gray-800 mb-1.5">
                        Contact Person *
                      </label>
                      <div className="relative">
                        <div className="absolute top-3 left-3 pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <input 
                          type="text" 
                          id="Contact_Person"
                          className={`w-full pl-9 pr-3 py-2 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm bg-gray-50 focus:bg-white ${
                            companyEditErrors.Contact_Person ? 'border-red-500' : 'border-gray-200'
                          }`}
                          name="Contact_Person"
                          value={companyEditForm.Contact_Person}
                          onChange={(e) => {
                            setCompanyEditForm({...companyEditForm, Contact_Person: e.target.value});
                            setCompanyEditErrors({...companyEditErrors, Contact_Person: ''});
                          }}
                          required
                          placeholder="e.g., John Smith"
                        />
                      </div>
                      {companyEditErrors.Contact_Person && (
                        <p className="text-red-500 text-xs mt-1">{companyEditErrors.Contact_Person}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Company Registration Number */}
                      <div>
                        <label htmlFor="Company_Reg" className="block text-sm font-semibold text-gray-800 mb-1.5">
                          Registration Number
                        </label>
                        <div className="relative">
                          <div className="absolute top-3 left-3 pointer-events-none">
                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                          </div>
                          <input 
                            type="text" 
                            id="Company_Reg"
                            className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm bg-gray-50 focus:bg-white"
                            name="Company_Reg"
                            value={companyEditForm.Company_Reg}
                            onChange={(e) => setCompanyEditForm({...companyEditForm, Company_Reg: e.target.value})}
                            placeholder="e.g., 2020/123456/07"
                          />
                        </div>
                      </div>

                      {/* VAT Number */}
                      <div>
                        <label htmlFor="Vat_Registration" className="block text-sm font-semibold text-gray-800 mb-1.5">
                          VAT Number
                        </label>
                        <div className="relative">
                          <div className="absolute top-3 left-3 pointer-events-none">
                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <input 
                            type="text" 
                            id="Vat_Registration"
                            className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm bg-gray-50 focus:bg-white"
                            name="Vat_Registration"
                            value={companyEditForm.Vat_Registration}
                            onChange={(e) => setCompanyEditForm({...companyEditForm, Vat_Registration: e.target.value})}
                            placeholder="e.g., 4123456789"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer with Gradient */}
                  <div className="px-5 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 rounded-b-xl flex justify-end gap-2.5">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all"
                      onClick={() => setShowCompanyEdit(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="px-5 py-2 text-sm font-semibold text-white bg-gradient-purple border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all shadow-md hover:shadow-lg compact-button"
                    >
                      <span className="flex items-center gap-1.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save Changes
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Password Setup Modal */}
        {showPasswordSetup && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="bg-blue-600 text-white p-4 rounded-t-lg">
                <h3 className="text-lg font-semibold">Complete Your Profile Setup</h3>
                <p className="text-blue-100 text-sm mt-1">Please set up your profile to continue</p>
              </div>
              
              <form onSubmit={handlePasswordSetupSubmit} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={passwordSetupData.name}
                      onChange={handlePasswordSetupChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        passwordErrors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your full name"
                    />
                    {passwordErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Position *
                    </label>
                    <input
                      type="text"
                      name="position"
                      value={passwordSetupData.position}
                      onChange={handlePasswordSetupChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        passwordErrors.position ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g. Procurement Manager"
                    />
                    {passwordErrors.position && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.position}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={passwordSetupData.password}
                      onChange={handlePasswordSetupChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        passwordErrors.password ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter a secure password"
                    />
                    {passwordErrors.password && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.password}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordSetupData.confirmPassword}
                      onChange={handlePasswordSetupChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        passwordErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Confirm your password"
                    />
                    {passwordErrors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.confirmPassword}</p>
                    )}
                    {passwordSetupData.password && passwordSetupData.confirmPassword && 
                     passwordSetupData.password === passwordSetupData.confirmPassword && (
                      <p className="text-green-500 text-xs mt-1">✓ Passwords match</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="submit"
                    disabled={
                      !passwordSetupData.name.trim() ||
                      !passwordSetupData.position.trim() ||
                      !passwordSetupData.password ||
                      passwordSetupData.password !== passwordSetupData.confirmPassword ||
                      Object.keys(passwordErrors).some(key => passwordErrors[key])
                    }
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Complete Setup
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Profile Edit Modal */}
        {showProfileEdit && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="bg-gradient-to-r from-cyan-600 to-blue-500 text-white p-4 rounded-t-lg">
                <h3 className="text-lg font-semibold">Edit Your Profile</h3>
                <p className="text-blue-100 text-sm mt-1">Update your personal information</p>
              </div>
              
              <form onSubmit={handleProfileEditSubmit} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      value={profileEditForm.name}
                      onChange={(e) => {
                        setProfileEditForm({...profileEditForm, name: e.target.value});
                        setProfileEditErrors({...profileEditErrors, name: ''});
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        profileEditErrors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your full name"
                    />
                    {profileEditErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{profileEditErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Position *
                    </label>
                    <input
                      type="text"
                      value={profileEditForm.position}
                      onChange={(e) => {
                        setProfileEditForm({...profileEditForm, position: e.target.value});
                        setProfileEditErrors({...profileEditErrors, position: ''});
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        profileEditErrors.position ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g. Procurement Manager"
                    />
                    {profileEditErrors.position && (
                      <p className="text-red-500 text-xs mt-1">{profileEditErrors.position}</p>
                    )}
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm text-gray-600 mb-3">Change Password (Optional)</p>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Password
                        </label>
                        <input
                          type="password"
                          value={profileEditForm.password}
                          onChange={(e) => {
                            setProfileEditForm({...profileEditForm, password: e.target.value});
                            setProfileEditErrors({...profileEditErrors, password: ''});
                          }}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            profileEditErrors.password ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter new password (min. 6 characters)"
                        />
                        {profileEditErrors.password && (
                          <p className="text-red-500 text-xs mt-1">{profileEditErrors.password}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          value={profileEditForm.confirmPassword}
                          onChange={(e) => {
                            setProfileEditForm({...profileEditForm, confirmPassword: e.target.value});
                            setProfileEditErrors({...profileEditErrors, confirmPassword: ''});
                          }}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            profileEditErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Confirm your new password"
                        />
                        {profileEditErrors.confirmPassword && (
                          <p className="text-red-500 text-xs mt-1">{profileEditErrors.confirmPassword}</p>
                        )}
                        {profileEditForm.password && profileEditForm.confirmPassword && 
                         profileEditForm.password === profileEditForm.confirmPassword && (
                          <p className="text-green-500 text-xs mt-1">✓ Passwords match</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowProfileEdit(false);
                      setProfileEditErrors({});
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!profileEditForm.name.trim() || !profileEditForm.position.trim()}
                    className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-500 text-white rounded-md hover:from-cyan-700 hover:to-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Update Profile
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Simple Debug Modal */}
        {settings?.debug_mode === '1' && showDebugModal && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto">
              <div className="bg-red-600 text-white p-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold">Debug Information</h3>
                <button 
                  onClick={() => setShowDebugModal(false)}
                  className="text-white hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6">
                <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm text-gray-100 overflow-auto">
                  <pre>{JSON.stringify({
                    dashboardData,
                    supplier,
                    applicationStatus,
                    statusLoading,
                    settings
                  }, null, 2)}</pre>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Email Modal */}
        {showAddEmail && (
          <div className="fixed inset-0 z-50 overflow-y-auto backdrop-blur-sm">
            <div className="flex items-center justify-center min-h-screen px-4 py-6">
              <div 
                className="fixed inset-0 bg-gradient-to-br from-gray-900/60 via-gray-900/50 to-gray-900/60 transition-opacity" 
                onClick={() => setShowAddEmail(false)} 
              />
              
              <div className="relative bg-white rounded-xl shadow-2xl transform transition-all w-full max-w-md border border-gray-100">
                <form onSubmit={handleEmailSubmit}>
                  {/* Header with Gradient */}
                  <div className="relative px-5 py-3.5 bg-gradient-primary rounded-t-xl compact-header">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-white">Add Email Address</h3>
                      </div>
                      <button
                        type="button"
                        className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-1 transition-all duration-200"
                        onClick={() => setShowAddEmail(false)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  {/* Body with Enhanced Styling */}
                  <div className="px-5 py-4 space-y-4">
                    {/* Email Input with Icon */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-800 mb-1.5">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                          </svg>
                        </div>
                        <input 
                          type="email" 
                          id="email"
                          className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm bg-gray-50 focus:bg-white" 
                          name="email"
                          value={emailForm.email}
                          onChange={(e) => setEmailForm({...emailForm, email: e.target.value})}
                          required
                          placeholder="Enter email address"
                        />
                      </div>
                    </div>

                    {/* Permissions Section with Cards */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Permissions</label>
                      <div className="space-y-2">
                        {/* Can Login Checkbox - Card Style */}
                        <div className={`relative p-3 rounded-lg border-2 transition-all cursor-pointer ${
                          emailForm.canLogin 
                            ? 'bg-blue-50 border-blue-300 shadow-sm' 
                            : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                        }`}>
                          <div className="flex items-start">
                            <input
                              type="checkbox"
                              id="canLogin"
                              checked={emailForm.canLogin}
                              onChange={(e) => setEmailForm({
                                ...emailForm, 
                                canLogin: e.target.checked,
                                viewOnly: e.target.checked ? emailForm.viewOnly : false,
                                sendLink: e.target.checked ? emailForm.sendLink : false
                              })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                            />
                            <div className="ml-2.5 flex-1">
                              <label htmlFor="canLogin" className="block text-sm font-medium text-gray-900 cursor-pointer">
                                Can Login to Supplier Portal
                              </label>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Allow this user to access the supplier portal
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* View Only Checkbox - Card Style */}
                        <div className={`relative p-3 rounded-lg border-2 transition-all ${
                          !emailForm.canLogin 
                            ? 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed' 
                            : emailForm.viewOnly
                              ? 'bg-blue-50 border-blue-300 shadow-sm cursor-pointer'
                              : 'bg-gray-50 border-gray-200 hover:border-gray-300 cursor-pointer'
                        }`}>
                          <div className="flex items-start">
                            <input
                              type="checkbox"
                              id="viewOnly"
                              checked={emailForm.viewOnly}
                              onChange={(e) => setEmailForm({...emailForm, viewOnly: e.target.checked})}
                              disabled={!emailForm.canLogin}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5 disabled:cursor-not-allowed"
                            />
                            <div className="ml-2.5 flex-1">
                              <label htmlFor="viewOnly" className={`block text-sm font-medium text-gray-900 ${!emailForm.canLogin ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                View Only Access
                              </label>
                              <p className="text-xs text-gray-500 mt-0.5">
                                User can view information but cannot make changes
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Send Link Section - Card Style */}
                    <div className={`relative p-3 rounded-lg border-2 transition-all ${
                      !emailForm.canLogin 
                        ? 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed' 
                        : emailForm.sendLink
                          ? 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-300 shadow-sm cursor-pointer'
                          : 'bg-gray-50 border-gray-200 hover:border-gray-300 cursor-pointer'
                    }`}>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="sendLink"
                          checked={emailForm.sendLink}
                          onChange={(e) => setEmailForm({...emailForm, sendLink: e.target.checked})}
                          disabled={!emailForm.canLogin}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:cursor-not-allowed"
                        />
                        <label htmlFor="sendLink" className={`ml-2.5 block text-sm font-medium text-gray-900 ${!emailForm.canLogin ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          Send Password Setup Link via Email
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer with Gradient */}
                  <div className="px-5 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 rounded-b-xl flex justify-end gap-2.5">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all"
                      onClick={() => setShowAddEmail(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="px-5 py-2 text-sm font-semibold text-white bg-gradient-primary border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all shadow-md hover:shadow-lg compact-button"
                    >
                      <span className="flex items-center gap-1.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Email
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Email Modal */}
        {showEditEmail && (
          <div className="fixed inset-0 z-50 overflow-y-auto backdrop-blur-sm">
            <div className="flex items-center justify-center min-h-screen px-4 py-6">
              <div 
                className="fixed inset-0 bg-gradient-to-br from-gray-900/60 via-gray-900/50 to-gray-900/60 transition-opacity" 
                onClick={() => setShowEditEmail(false)} 
              />
              
              <div className="relative bg-white rounded-xl shadow-2xl transform transition-all w-full max-w-md border border-gray-100">
                <form onSubmit={handleEmailUpdate}>
                  {/* Header with Gradient */}
                  <div className="relative px-5 py-3.5 bg-gradient-primary rounded-t-xl compact-header">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-white">Edit Email Settings</h3>
                      </div>
                      <button
                        type="button"
                        className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-1 transition-all duration-200"
                        onClick={() => setShowEditEmail(false)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  {/* Body with Enhanced Styling */}
                  <div className="px-5 py-4 space-y-4">
                    {/* Email Input with Icon */}
                    <div>
                      <label htmlFor="emailEdit" className="block text-sm font-semibold text-gray-800 mb-1.5">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                          </svg>
                        </div>
                        <input 
                          type="email" 
                          id="emailEdit"
                          className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm bg-gray-50 focus:bg-white" 
                          name="email"
                          value={emailForm.email}
                          onChange={(e) => setEmailForm({...emailForm, email: e.target.value})}
                          required
                        />
                      </div>
                    </div>

                    {/* Permissions Section with Cards */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">User Permissions</label>
                      <div className="space-y-2">
                        {/* Can Login Checkbox - Card Style */}
                        <div className={`relative p-3 rounded-lg border-2 transition-all cursor-pointer ${
                          emailForm.canLogin 
                            ? 'bg-blue-50 border-blue-300 shadow-sm' 
                            : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                        }`}>
                          <div className="flex items-start">
                            <input
                              type="checkbox"
                              id="canLoginEdit"
                              checked={emailForm.canLogin}
                              onChange={(e) => setEmailForm({
                                ...emailForm, 
                                canLogin: e.target.checked,
                                viewOnly: e.target.checked ? emailForm.viewOnly : false,
                                sendLink: e.target.checked ? emailForm.sendLink : false
                              })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                            />
                            <div className="ml-2.5 flex-1">
                              <label htmlFor="canLoginEdit" className="block text-sm font-medium text-gray-900 cursor-pointer">
                                Can Login to Supplier Portal
                              </label>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Allow this user to access the supplier portal
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* View Only Checkbox - Card Style */}
                        <div className={`relative p-3 rounded-lg border-2 transition-all ${
                          !emailForm.canLogin 
                            ? 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed' 
                            : emailForm.viewOnly
                              ? 'bg-blue-50 border-blue-300 shadow-sm cursor-pointer'
                              : 'bg-gray-50 border-gray-200 hover:border-gray-300 cursor-pointer'
                        }`}>
                          <div className="flex items-start">
                            <input
                              type="checkbox"
                              id="viewOnlyEdit"
                              checked={emailForm.viewOnly}
                              onChange={(e) => setEmailForm({...emailForm, viewOnly: e.target.checked})}
                              disabled={!emailForm.canLogin}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5 disabled:cursor-not-allowed"
                            />
                            <div className="ml-2.5 flex-1">
                              <label htmlFor="viewOnlyEdit" className={`block text-sm font-medium text-gray-900 ${!emailForm.canLogin ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                View Only Access
                              </label>
                              <p className="text-xs text-gray-500 mt-0.5">
                                User can view information but cannot make changes
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Send Link Section - Card Style */}
                    <div className={`relative p-3 rounded-lg border-2 transition-all ${
                      !emailForm.canLogin 
                        ? 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed' 
                        : emailForm.sendLink
                          ? 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-300 shadow-sm cursor-pointer'
                          : 'bg-gray-50 border-gray-200 hover:border-gray-300 cursor-pointer'
                    }`}>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="sendLinkEdit"
                          checked={emailForm.sendLink}
                          onChange={(e) => setEmailForm({...emailForm, sendLink: e.target.checked})}
                          disabled={!emailForm.canLogin}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:cursor-not-allowed"
                        />
                        <label htmlFor="sendLinkEdit" className={`ml-2.5 block text-sm font-medium text-gray-900 ${!emailForm.canLogin ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          Send Password Reset Link via Email
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer with Gradient */}
                  <div className="px-5 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 rounded-b-xl flex justify-end gap-2.5">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all"
                      onClick={() => setShowEditEmail(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="px-5 py-2 text-sm font-semibold text-white bg-gradient-primary border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all shadow-md hover:shadow-lg compact-button"
                    >
                      <span className="flex items-center gap-1.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Update Email
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Logo Upload Modal */}
        {showLogoUpload && (
          <SupplierLogoUpload
            currentLogo={supplierData?.supplier_logo?.base64 || supplierData?.supplier_logo?.file_url || null}
            supplierCode={supplierData?.BP_Code}
            supplierName={supplierData?.BP_Name || 'Supplier'}
            onLogoUpdated={async () => {
              // Refresh dashboard data to show new logo
              await fetchDashboardData();
            }}
            onClose={() => setShowLogoUpload(false)}
          />
        )}
      </div>
    );
  } catch (error) {
    console.error('Dashboard render error:', error);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h4 className="text-xl font-semibold text-gray-800 mb-2">Error loading dashboard</h4>
            <p className="text-gray-600 mb-6">Please refresh and try again.</p>
            <button 
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition duration-200"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default SupplierDashboard;
